import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import FuelAutoComplete from '@/components/fuel-autocomplete';
import SimplePlayer from '@/components/simple-player/SimplePlayer';
import { useProgress } from '@/contexts/ProgressProvider';
import { useWebSocketContext } from '@/contexts/WebSocketProvider';
import VnModalBody from '@/modals/components/VnModalBody';
import VnModalFooter from '@/modals/components/VnModalFooter';
import { useModalContext } from '@/modals/VNModalProvider';
import { FuelEntityType, FuelPortalService } from '@/services';
import { FuelToolsApiService } from '@/services/FuelToolsApi/fuelToolsApi.service';
import { applicationDataSelector } from '@/store/slices/application-data';
import {
  isInputOverallStatusConnected,
  isInputOverallStatusDisconnected,
  isInputOverallStatusLive,
  isInputOverallStatusLiveSessionActive,
  logInputOverallStatus,
} from '@/models/input';
import useAppUtils from '@/utils/appUtils';

import { parseInputStatusUpdateMessage, resolveInputStatusUpdate, type InputStatusUpdatePayload } from './inputWebsocketHelper';
import InputOverallStatusDisplay from './InputOverallStatusDisplay';

const PREVIEW_MAX_WAIT_SECONDS = 180;
const PREVIEW_MAX_WAIT_MS = PREVIEW_MAX_WAIT_SECONDS * 1000;

function formatDurationMinutesSeconds(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m${seconds}s`;
}

const PREVIEW_MAX_WAIT_DURATION = formatDurationMinutesSeconds(PREVIEW_MAX_WAIT_SECONDS);
const PREVIEW_LOADING_MESSAGE = `Preview may take up to ${PREVIEW_MAX_WAIT_DURATION} to load.`;
const PREVIEW_FAILED_MESSAGE =
  'Preview failed to load. The stream may not be available yet — check the encoder status and try again.';
const PREVIEW_UNAVAILABLE_MESSAGE = 'Live preview is not available until the input source is connected.';

type PreviewLoadState = 'loading' | 'ready' | 'failed';

interface PreviewSession {
  jobId: string | null;
  livePreviewUrl: string | null;
  previewUnavailable: boolean;
}

const idsMatch = (a?: string | null, b?: string | null) => !!a && !!b && a.toLowerCase() === b.toLowerCase();

const createPreviewSession = (livePreviewState?: {
  jobId?: string | null;
  livePreviewUrl?: string | null;
  previewUnavailable?: boolean;
}): PreviewSession => ({
  jobId: livePreviewState?.jobId ?? null,
  livePreviewUrl: livePreviewState?.livePreviewUrl ?? null,
  previewUnavailable: livePreviewState?.previewUnavailable === true,
});

const LivePreviewInput = ({ input, livePreviewState, onClose }) => {
  const { setProgress } = useProgress();
  const { vnConfirm, alert } = useAppUtils();
  const { ouData } = useSelector(applicationDataSelector);
  const jobIdRef = useRef(livePreviewState?.jobId ?? null);
  const isManualActiveRef = useRef(false);
  const isStartingPreviewRef = useRef(false);
  const prevOverallStatusRef = useRef<string | null>(input?.overallStatus ?? null);
  const previewUrlRef = useRef(livePreviewState?.livePreviewUrl ?? null);
  const statusAppliedSeqRef = useRef(0);
  const overallStatusRef = useRef<string | null>(input?.overallStatus ?? null);
  const selectedProgramRef = useRef(null);
  const { setPreventClose } = useModalContext();
  const { lastJsonMessage } = useWebSocketContext();
  const lastJsonMessageRef = useRef(lastJsonMessage);
  const [previewSession, setPreviewSession] = useState<PreviewSession>(() => createPreviewSession(livePreviewState));
  const [isStartingPreview, setIsStartingPreview] = useState(false);
  const [allLivePrograms, setAllLivePrograms] = useState([]);
  const [selectedLiveProgram, setSelectedLiveProgram] = useState(null);
  const [overallStatus, setOverallStatusState] = useState<string | null>(input?.overallStatus ?? null);
  const setOverallStatus = useCallback((status: string | null) => {
    overallStatusRef.current = status;
    setOverallStatusState(status);
  }, []);
  const [statusProgramId, setStatusProgramId] = useState<string | null>(null);
  const [activeProgramId, setActiveProgramId] = useState<string | null>(null);
  const lastProcessedMessageKeyRef = useRef<string | null>(null);

  // isManualActive: whether a manual start has been initiated in this session
  // OR carried over from a previous session (passed via livePreviewState)
  const [isManualActive, setIsManualActive] = useState(livePreviewState?.isManualActive ?? false);
  const [isFetchingStatus, setIsFetchingStatus] = useState(true);
  const [previewLoadState, setPreviewLoadState] = useState<PreviewLoadState>('loading');

  // Notification to show after closing and re-opening when manual is active
  const [showManualActiveNotification, setShowManualActiveNotification] = useState(livePreviewState?.isManualActive ?? false);

  // Inline error: shown when Manual Start clicked with no program selected
  const [showNoProgramError, setShowNoProgramError] = useState(false);

  const previewUrl = previewSession.livePreviewUrl;

  useEffect(() => {
    const connected = isInputOverallStatusConnected(overallStatus);
    if (!previewUrl) {
      setPreviewLoadState(connected ? 'loading' : 'failed');
    }
  }, [previewUrl, overallStatus]);

  useEffect(() => {
    if (previewUrl) {
      setPreviewLoadState('loading');
    }
  }, [previewUrl]);

  const handlePlaybackReady = useCallback(
    (success: boolean) => {
      if (success) {
        setPreviewLoadState('ready');
        return;
      }
      setPreviewLoadState('failed');
      alert(PREVIEW_FAILED_MESSAGE, 'warning');
    },
    [alert]
  );

  // Emergency stop: only stop the job preview stream, NOT the manual live recording
  const handleEmergencyStop = useCallback(() => {
    if (jobIdRef.current) {
      void FuelToolsApiService.livePreviewStopSafe(jobIdRef.current);
    }
    // Do NOT call manualLiveStop here — manual recording continues independently
  }, []);

  useEffect(() => {
    if (isManualActive) {
      setPreventClose(true, {
        title: 'Existing Live Preview',
        message: 'The manual start will continue until you select manual stop.',
        confirmText: 'OK',
      });
    } else {
      setPreventClose(false);
    }

    // Cleanup: always reset when the component unmounts
    return () => setPreventClose(false);
  }, [isManualActive]);

  const stopLivePreview = useCallback(() => {
    if (jobIdRef.current) {
      FuelToolsApiService.livePreviewStop(jobIdRef.current);
      jobIdRef.current = null;
    }

    isStartingPreviewRef.current = false;
    setIsStartingPreview(false);
    setPreviewSession({
      jobId: null,
      livePreviewUrl: null,
      previewUnavailable: true,
    });
    setPreviewLoadState('failed');
  }, []);

  const startLivePreview = useCallback(async () => {
    if (!input?.id || !ouData?.ouId || isStartingPreviewRef.current) {
      return;
    }

    if (previewUrlRef.current) {
      return;
    }

    isStartingPreviewRef.current = true;
    setIsStartingPreview(true);
    try {
      const response = await FuelToolsApiService.livePreviewStart({ inputId: input.id, ouId: ouData.ouId });
      if (!response?.livePreviewUrl) {
        alert('Live Preview URL is not available', 'error');
        return;
      }

      jobIdRef.current = response.jobId;
      setPreviewSession({
        jobId: response.jobId,
        livePreviewUrl: response.livePreviewUrl,
        previewUnavailable: false,
      });
      setPreviewLoadState('loading');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start Live Preview';
      alert(message, 'error');
      console.error('Live Preview Error:', error);
    } finally {
      isStartingPreviewRef.current = false;
      setIsStartingPreview(false);
    }
  }, [alert, input?.id, ouData?.ouId]);

  const applyConnectionTransition = useCallback(
    async (nextOverallStatus: string) => {
      const previousStatus = prevOverallStatusRef.current;
      const wasConnected = isInputOverallStatusConnected(previousStatus);
      const nowConnected = isInputOverallStatusConnected(nextOverallStatus);
      const nowDisconnected = isInputOverallStatusDisconnected(nextOverallStatus);

      prevOverallStatusRef.current = nextOverallStatus;

      if (!wasConnected && nowConnected) {
        await startLivePreview();
      } else if (wasConnected && nowDisconnected) {
        stopLivePreview();
      }
    },
    [startLivePreview, stopLivePreview]
  );

  const applyStatusUpdate = useCallback(
    (status: { overallStatus?: string; liveProgramIds?: string[] }) => {
      if (status.overallStatus != null) {
        logInputOverallStatus('LivePreview status applied', status.overallStatus);
        setOverallStatus(status.overallStatus);
        const isLiveSessionActive = isInputOverallStatusLiveSessionActive(status.overallStatus);
        setIsManualActive(isLiveSessionActive);
        isManualActiveRef.current = isLiveSessionActive;
        if (isInputOverallStatusLive(status.overallStatus) && status.liveProgramIds?.[0]) {
          setActiveProgramId(status.liveProgramIds[0]);
        } else if (!isLiveSessionActive) {
          setActiveProgramId(null);
        }
      }

      if (status.liveProgramIds?.[0]) {
        setStatusProgramId(status.liveProgramIds[0]);
      }
    },
    [setOverallStatus]
  );

  const handleWebSocketStatusUpdate = useCallback(
    async (payload: InputStatusUpdatePayload) => {
      try {
        const status = await resolveInputStatusUpdate(payload);
        if (status.overallStatus == null) {
          return;
        }

        statusAppliedSeqRef.current += 1;
        applyStatusUpdate(status);
        await applyConnectionTransition(status.overallStatus);
      } catch (error) {
        console.error('Failed to apply input status update', error);
      }
    },
    [applyStatusUpdate, applyConnectionTransition]
  );

  // Existing data fetching
  const queryParams = useMemo(
    () => ({
      ouId: ouData?.ouId,
      page: 0,
      size: 1000,
      keyword: '',
      sortBy: 'name',
      orderBy: 'asc',
      enabledOnly: true,
      showDeleted: null,
    }),
    [ouData?.ouId]
  );
  const fetchCurrentStatus = useCallback(
    async (inputId: string) => {
      if (!inputId) return;
      const fetchSeq = statusAppliedSeqRef.current;
      try {
        setIsFetchingStatus(true);
        const status = await resolveInputStatusUpdate({ inputId });

        if (fetchSeq !== statusAppliedSeqRef.current) {
          return;
        }

        if (status.overallStatus != null) {
          applyStatusUpdate(status);
          prevOverallStatusRef.current = status.overallStatus;
        }
      } catch (error) {
        console.error('Failed to fetch initial status', error);
      } finally {
        setIsFetchingStatus(false);
      }
    },
    [applyStatusUpdate]
  );

  const getLivePrograms = useCallback(async () => {
    if (!ouData?.ouId) return;
    try {
      setProgress(true);
      const response = await FuelPortalService.getList(FuelEntityType.LiveProgram, queryParams);
      setAllLivePrograms(response?.data || []);
    } catch (ex) {
      console.error('Error fetching live programs:', ex);
    } finally {
      setProgress(false);
    }
  }, [queryParams, setProgress]);

  const handleWebSocketStatusUpdateRef = useRef(handleWebSocketStatusUpdate);
  handleWebSocketStatusUpdateRef.current = handleWebSocketStatusUpdate;
  lastJsonMessageRef.current = lastJsonMessage;

  const processWebSocketMessage = useCallback((message: unknown) => {
    const payload = parseInputStatusUpdateMessage(message);
    if (!payload || !idsMatch(payload.inputId, input?.id)) {
      return;
    }

    let messageKey: string;
    try {
      messageKey = JSON.stringify(message);
    } catch {
      return;
    }

    const incomingStatus = payload.overallStatus?.toLowerCase();
    const currentStatus = overallStatusRef.current?.toLowerCase();
    const alreadyApplied =
      messageKey === lastProcessedMessageKeyRef.current &&
      incomingStatus != null &&
      incomingStatus === currentStatus;

    if (alreadyApplied) {
      console.log('[InputStatus] LivePreview skipped duplicate WebSocket update', {
        inputId: payload.inputId,
        overallStatus: payload.overallStatus,
      });
      return;
    }

    console.log('[InputStatus] LivePreview processing WebSocket update', {
      inputId: payload.inputId,
      overallStatus: payload.overallStatus,
    });

    lastProcessedMessageKeyRef.current = messageKey;
    void handleWebSocketStatusUpdateRef.current(payload);
  }, [input?.id]);

  useEffect(() => {
    if (!input?.id) {
      return;
    }

    const initialStatus = input?.overallStatus ?? null;
    lastProcessedMessageKeyRef.current = null;
    statusAppliedSeqRef.current = 0;
    setOverallStatus(initialStatus);
    prevOverallStatusRef.current = initialStatus;
    getLivePrograms();
    void fetchCurrentStatus(input.id).then(() => {
      const message = lastJsonMessageRef.current;
      if (message != null) {
        processWebSocketMessage(message);
      }
    });
  }, [input?.id]);

  useEffect(() => {
    if (lastJsonMessage == null || !input?.id) {
      return;
    }

    processWebSocketMessage(lastJsonMessage);
  }, [lastJsonMessage, input?.id, processWebSocketMessage]);

  const dropdownProgramId = activeProgramId ?? statusProgramId;

  const liveProgramOptions = useMemo(() => {
    if (activeProgramId) {
      return allLivePrograms.filter((program) => idsMatch(program.id, activeProgramId));
    }
    return allLivePrograms;
  }, [activeProgramId, allLivePrograms]);

  useEffect(() => {
    if (!dropdownProgramId) return;

    const matchingProgram = liveProgramOptions.find((program) => idsMatch(program.id, dropdownProgramId));
    if (matchingProgram) {
      setSelectedLiveProgram(matchingProgram);
      selectedProgramRef.current = matchingProgram;
      return;
    }

    let cancelled = false;
    const loadProgram = async () => {
      try {
        const program = await FuelPortalService.getEntityById(FuelEntityType.LiveProgram, dropdownProgramId, null);
        if (cancelled || !program) return;

        setAllLivePrograms((prev) => (prev.some((item) => idsMatch(item.id, program.id)) ? prev : [...prev, program]));
        setSelectedLiveProgram(program);
        selectedProgramRef.current = program;
      } catch (error) {
        console.error('Failed to fetch live program for input status', error);
      }
    };

    loadProgram();
    return () => {
      cancelled = true;
    };
  }, [dropdownProgramId, liveProgramOptions]);

  const isStreamActive = isManualActive;
  const isSourceConnected = isInputOverallStatusConnected(overallStatus ?? input?.overallStatus);
  const manualControlsDisabled = !isSourceConnected || isFetchingStatus || isStartingPreview;
  const showStatusOverlay = !isFetchingStatus || overallStatus != null;
  const playerOverlayMessage = !isSourceConnected
    ? PREVIEW_UNAVAILABLE_MESSAGE
    : previewLoadState === 'failed'
      ? PREVIEW_FAILED_MESSAGE
      : PREVIEW_LOADING_MESSAGE;
  const showPlayer = isSourceConnected && !!previewUrl;

  useEffect(() => {
    jobIdRef.current = previewSession.jobId;
    previewUrlRef.current = previewSession.livePreviewUrl;
  }, [previewSession.jobId, previewSession.livePreviewUrl]);

  // Sync manual active state from livePreviewState (for re-open scenario)
  useEffect(() => {
    if (livePreviewState?.isManualActive) {
      isManualActiveRef.current = true;
      setIsManualActive(true);
    }
  }, [livePreviewState?.isManualActive]);

  const handleLiveProgramChange = useCallback((val) => {
    setSelectedLiveProgram(val);
    selectedProgramRef.current = val;
    if (val) setShowNoProgramError(false);
  }, []);

  const handleAction = useCallback(
    async (actionType: string) => {
      const isStart = actionType === 'Start';

      if (!selectedLiveProgram) {
        setShowNoProgramError(true);
        return;
      }
      setShowNoProgramError(false);

      const isOk = await vnConfirm(
        isStart
          ? {
            title: 'Confirm Start',
            message: (
              <Box>
                <Typography variant="body2" gutterBottom>
                  Confirm you want to start this live program.
                </Typography>
                <Typography variant="body2">Streaming may take up to {PREVIEW_MAX_WAIT_DURATION} to begin.</Typography>
              </Box>
            ),
            confirmText: 'Start',
            cancelText: 'Cancel',
          }
          : {
            title: 'Confirm Stop',
            message: (
              <Box>
                <Typography variant="body2" gutterBottom>
                  Confirm you want to stop this live program's stream.
                </Typography>
                <Typography variant="body2">This will stop streaming and recording into the asset library.</Typography>
              </Box>
            ),
            confirmText: 'Stop',
            cancelText: 'Cancel',
          }
      );

      if (!isOk) return;

      try {
        setProgress(true);
        const payload = {
          liveProgramId: selectedLiveProgram?.id,
          liveProgramName: selectedLiveProgram?.name,
          inputDetails: input,
          actionType,
        };

        if (isStart) {
          setShowManualActiveNotification(false);
          const response = await FuelToolsApiService.manualLiveStart(payload);
          if (response.message) {
            alert('Manual recording will start in a moment.');
          }
        } else {
          await FuelToolsApiService.manualLiveStop(payload);
          setShowManualActiveNotification(false);
        }
      } catch (ex) {
        console.error(`Failed to ${actionType} manual live schedule:`, ex);
      } finally {
        setProgress(false);
      }
    },
    [input, selectedLiveProgram]
  );

  // Handle close: stop the preview stream but NOT the manual live recording
  const handleClose = useCallback(() => {
    // Stop only the preview job
    // if (jobIdRef.current) {
    //   FuelToolsApiService.livePreviewStop(jobIdRef.current);
    // }
    // // If manual was active, show a notification AFTER closing
    // if (isManualActiveRef.current) {
    //   // Delay slightly so parent can show the notification after modal closes
    //   setTimeout(() => {
    //     alert('The manual start of this input will continue until the manual stop is initiated.', 'info');
    //   }, 300);
    // }
    // onClose?.();
  }, [onClose]);

  // On unmount, only stop the preview stream — NOT the manual recording
  useEffect(() => {
    const handleTabClose = () => {
      handleEmergencyStop();
    };

    window.addEventListener('beforeunload', handleTabClose);
    return () => {
      handleEmergencyStop();
      window.removeEventListener('beforeunload', handleTabClose);
    };
  }, [handleEmergencyStop]);

  return (
    <>
      <VnModalBody>
        {/* Error: no program selected on Manual Start attempt */}
        {showNoProgramError && (
          <Alert severity="error" variant="outlined" onClose={() => setShowNoProgramError(false)} sx={{ mb: 2 }}>
            Select a live program before starting the stream.
          </Alert>
        )}

        {/* Notification: shown when re-opening after a manual start */}
        {showManualActiveNotification && (
          <Alert severity="info" onClose={() => setShowManualActiveNotification(false)} sx={{ mb: 2 }}>
            The manual start of this input will continue until the manual stop is initiated.
          </Alert>
        )}

        {input?.name ? (
          <Typography component="h2" variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
            {input.name}
          </Typography>
        ) : null}

        {/* Video Player — 16:9 placeholder until loaded; then Video.js fluid sizing */}
        <Box
          sx={{
            mb: 2,
            bgcolor: 'black',
            width: '100%',
            ...(previewLoadState !== 'ready' ? { aspectRatio: '16 / 9' } : {}),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {previewLoadState !== 'ready' && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                zIndex: 2,
                color: 'white',
                width: '100%',
                px: 2,
              }}
            >
              {(previewLoadState === 'loading' || isStartingPreview) && isSourceConnected && (
                <CircularProgress size={50} thickness={4} sx={{ mb: 3, color: 'white' }} />
              )}

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: 'rgba(255, 255, 255, 0.8) !important',
                  letterSpacing: '0.5px',
                }}
              >
                {playerOverlayMessage}
              </Typography>

              {(previewLoadState === 'loading' || isStartingPreview) && isSourceConnected && (
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 400,
                    color: 'rgba(255, 255, 255, 0.8) !important',
                  }}
                >
                  Select a live program to start streaming now.
                </Typography>
              )}
            </Box>
          )}
          {showStatusOverlay ? (
            <InputOverallStatusDisplay overallStatus={overallStatus ?? input?.overallStatus} overlay />
          ) : null}
          {showPlayer ? (
            <SimplePlayer
              key={previewSession.jobId}
              videoId={`live-preview-${previewSession.jobId}`}
              srcUrl={previewUrl}
              autoPlay
              muted
              hidden={previewLoadState !== 'ready'}
              maxWaitMs={PREVIEW_MAX_WAIT_MS}
              onPlaybackReady={handlePlaybackReady}
            />
          ) : null}
        </Box>

        <Box
          sx={{
            display: 'flex',
            // alignItems: 'flex-start',
            // gap: 2,
            // px: 1,
          }}
        >
          {/* Dropdown */}
          <Box sx={{ flex: 1 }}>
            <FuelAutoComplete
              key={dropdownProgramId ?? selectedLiveProgram?.id ?? 'live-program-select'}
              id="live-program-select"
              items={liveProgramOptions}
              itemText="name"
              itemKey="id"
              labelText="Live Program"
              placeHolderText="Select Live Program"
              helperText="Select the Live Feed you want to Preview, Start or Stop"
              multiSelect={false}
              defaultValue={selectedLiveProgram}
              disabled={!!activeProgramId}
              onChange={handleLiveProgramChange}
            />
            {/* <Typography sx={{ pl: 1 }}>Select the Live Feed you want to Preview, Start or Stop</Typography> */}
          </Box>

          {/* Action Buttons — aligned to the right of the dropdown */}
        </Box>
      </VnModalBody>
      <VnModalFooter
        right={
          <>
            <Button
              variant="contained"
              color={isStreamActive ? 'error' : 'success'}
              onClick={() => handleAction('Start')}
              disabled={manualControlsDisabled || isStreamActive}
              size="small"
            >
              Manual Start
            </Button>

            <Button
              variant="text"
              size="small"
              color="success"
              disabled={manualControlsDisabled || !isStreamActive}
              onClick={() => handleAction('End')}
              className="manual-stop-btn"
            >
              Manual Stop
            </Button>
          </>
        }
      />
    </>
  );
};

export default LivePreviewInput;
